import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "@material-ui/core/styles";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

export default function FilterChips({
  selectedTags = [],
  selectedUsers = [],
  selectedWhatsapp = [],
  selectedStatus = [],
  dateStart = "",
  dateEnd = "",
  onRemove,
  openFilter,
}) {
  const theme = useTheme();
  const isLight = theme?.mode === "light" || theme?.palette?.type === "light";

  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);

  // Collect all active filter items
  const items = [];

  selectedTags.forEach((t) => {
    const label = typeof t === "object" ? t.name : String(t);
    const id = typeof t === "object" ? t.id : t;
    if (label) items.push({ key: id, label, type: "tag" });
  });

  selectedUsers.forEach((u) => {
    const label = typeof u === "object" ? u.name : String(u);
    const id = typeof u === "object" ? u.id : u;
    if (label) items.push({ key: id, label, type: "user" });
  });

  selectedWhatsapp.forEach((w) => {
    const label = typeof w === "object" ? w.name : String(w);
    const id = typeof w === "object" ? w.id : w;
    if (label) items.push({ key: id, label, type: "whatsapp" });
  });

  selectedStatus.forEach((s) => {
    const label = typeof s === "object" ? s.name : String(s);
    const id = typeof s === "object" ? s.status : s;
    if (label) items.push({ key: id, label, type: "status" });
  });

  if (dateStart) {
    items.push({
      key: "dateStart",
      label: `De ${formatDate(dateStart)}`,
      type: "dateStart",
    });
  }

  if (dateEnd) {
    items.push({
      key: "dateEnd",
      label: `Até ${formatDate(dateEnd)}`,
      type: "dateEnd",
    });
  }

  // Check overflow when items change or window resizes
  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current || !measureRef.current) return;
      const availableWidth =
        containerRef.current.clientWidth ||
        (containerRef.current.parentElement
          ? containerRef.current.parentElement.clientWidth
          : window.innerWidth - 32);
      const totalWidth = measureRef.current.scrollWidth;
      setIsOverflow(totalWidth > availableWidth);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [items.length]);

  if (items.length === 0) return null;

  const chipBoxStyle = {
    display: "inline-flex",
    alignItems: "center",
    height: "28px",
    padding: "0 10px",
    borderRadius: "15px",
    backgroundColor: isLight ? "#EEF3FF" : "#0c1210",
    color: isLight ? "#1e293b" : "#f1f5f9",
    border: `1px solid ${isLight ? "#bfdbfe" : "#1f3a2b"}`,
    fontSize: "12px",
    fontWeight: 500,
    whiteSpace: "nowrap",
    boxShadow: isLight
      ? "0 1px 2px rgba(0,0,0,0.05)"
      : "0 1px 2px rgba(0,0,0,0.4)",
    cursor: "default",
    userSelect: "none",
    flexShrink: 0,
    boxSizing: "border-box",
  };

  const closeBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "6px",
    cursor: "pointer",
    padding: "2px",
    borderRadius: "50%",
    lineHeight: 1,
    opacity: 0.7,
    border: "none",
    background: "transparent",
    color: "inherit",
    outline: "none",
  };

  const aggregateBoxStyle = {
    ...chipBoxStyle,
    cursor: "pointer",
    fontWeight: 600,
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: "3px",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* Hidden off-screen measurement container that always has all items */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: -9999,
          left: -9999,
          visibility: "hidden",
          pointerEvents: "none",
          display: "inline-flex",
          flexWrap: "nowrap",
          gap: "3px",
        }}
      >
        {items.map((item) => (
          <div key={`m-${item.type}-${item.key}`} style={chipBoxStyle}>
            <span>{item.label}</span>
            <span style={closeBtnStyle}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </span>
          </div>
        ))}
      </div>

      {isOverflow ? (
        <div
          style={aggregateBoxStyle}
          onClick={openFilter}
          role="button"
          tabIndex={0}
          title="Abrir filtros"
        >
          <span>{`Filter (+${items.length})`}</span>
        </div>
      ) : (
        items.map((item) => (
          <div key={`${item.type}-${item.key}`} style={chipBoxStyle}>
            <span>{item.label}</span>
            <button
              type="button"
              style={closeBtnStyle}
              onClick={(e) => {
                e.stopPropagation();
                if (onRemove) onRemove(item.key, item.type);
              }}
              title="Remover filtro"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        ))
      )}
    </div>
  );
}